import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { stripCategoryPrefix } from '../utils/categoryKeywords';
import { archiveItem, ENTITY_TYPES, ARCHIVE_REASONS } from '../utils/archiveManager';
import categoryApi from '../services/categoryApi';

const CategoryContext = createContext();

// Default categories for new users (werden nur angelegt, wenn ein Nutzer
// noch nie Kategorien hatte - weder im Backend noch als lokale Altlast)
const DEFAULT_CATEGORIES = ['Arbeit', 'Privat', 'Entwicklung', 'Nachrichten', 'Shopping', 'Bildung'];

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};

export const CategoryProvider = ({ children }) => {
  const { user } = useAuth();

  // Nach außen (Konsumenten wie CategoryManagement.js, BookmarkForm.js, ...)
  // bleibt "categories" ein einfaches String-Array (Namen) - unverändert
  // gegenüber dem bisherigen, rein lokalen Modell. Intern wird zusätzlich
  // categoryObjects (mit Backend-IDs) gepflegt, damit removeCategory() die
  // richtige ID für DELETE /api/categories/{id} findet.
  const [categories, setCategories] = useState([]);
  const [categoryObjects, setCategoryObjects] = useState([]); // [{id, name}]
  const [loading, setLoading] = useState(false);

  const currentUserRef = useRef(null);

  // Kategorien vom Backend laden, sobald ein Nutzer eingeloggt ist.
  // Einmal-Migration: vorhandene rein lokale Alt-Kategorien (aus der Zeit vor
  // der Backend-Anbindung) werden hochgeladen, falls das Backend noch leer ist.
  useEffect(() => {
    if (!user?.email) {
      setCategories([]);
      setCategoryObjects([]);
      currentUserRef.current = null;
      return;
    }

    if (currentUserRef.current === user.email) {
      return; // schon für diesen Nutzer geladen
    }
    currentUserRef.current = user.email;

    const loadCategories = async () => {
      setLoading(true);
      try {
        let serverCategories = await categoryApi.getAll();

        if (!serverCategories || serverCategories.length === 0) {
          // Migration: lokale Altlasten aus der Zeit vor der Backend-Anbindung
          // (Stand vor v0.9.14-Backend-Umstellung) einmalig hochladen.
          const legacyKey = `categories_${user.email}`;
          let legacyNames = [];
          try {
            const raw = localStorage.getItem(legacyKey);
            const parsed = raw ? JSON.parse(raw) : null;
            if (Array.isArray(parsed) && parsed.length > 0) {
              legacyNames = parsed;
            }
          } catch {
            // Altdaten korrupt/unlesbar - ignorieren, unten greifen dann die Defaults
          }

          const namesToCreate = legacyNames.length > 0 ? legacyNames : DEFAULT_CATEGORIES;

          for (const name of namesToCreate) {
            try {
              await categoryApi.create(name);
            } catch (err) {
              // z.B. 409 (Name existiert bereits, sollte hier eigentlich nicht
              // vorkommen da Backend ja gerade leer war) - einzelne fehlgeschlagene
              // Kategorie überspringen, Rest trotzdem anlegen
              console.warn(`Kategorie "${name}" konnte nicht migriert werden:`, err.message);
            }
          }

          try {
            localStorage.removeItem(legacyKey);
          } catch {
            // unkritisch
          }

          serverCategories = await categoryApi.getAll();
        }

        setCategoryObjects(serverCategories || []);
        setCategories((serverCategories || []).map(c => c.name));
      } catch (err) {
        console.error('❌ Kategorien konnten nicht geladen werden:', err);
        setCategories([]);
        setCategoryObjects([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [user]);

  const addCategory = async (categoryName, options = {}) => {
    if (!categoryName || !categoryName.trim()) return false;
    const trimmed = categoryName.trim();

    if (categories.includes(trimmed)) {
      return false; // Category already exists (Client-seitiger Schnellcheck)
    }

    try {
      const created = await categoryApi.create(trimmed);
      setCategoryObjects(prev => [...prev, created]);
      setCategories(prev => [...prev, created.name]);

      // Sicherheits-Snapshot beim Anlegen (nur wenn "Automatische Datensicherung" aktiv ist).
      // Wird bei einer Wiederherstellung aus dem Archiv übersprungen (skipArchiveSnapshot),
      // damit das Archiv nicht bei jedem Wiederherstellen einen neuen Eintrag anlegt.
      if (!options.skipArchiveSnapshot) {
        archiveItem(
          ENTITY_TYPES.CATEGORY,
          ARCHIVE_REASONS.AUTO_SNAPSHOT,
          { id: created.id, name: created.name },
          { label: created.name }
        );
      }

      return true;
    } catch (err) {
      // 409 Conflict (Name existiert bereits serverseitig, z.B. durch ein
      // zweites Gerät) oder Netzwerkfehler - beides als "nicht angelegt" werten
      console.error('❌ Kategorie konnte nicht angelegt werden:', err);
      return false;
    }
  };

  const removeCategory = async (categoryName) => {
    const match = categoryObjects.find(c => c.name === categoryName);
    if (!match) {
      console.warn(`Kategorie "${categoryName}" nicht gefunden, kann nicht gelöscht werden`);
      return;
    }

    // Papierkorb: gelöschte Kategorie immer archivieren, damit sie wiederherstellbar
    // bleibt - VOR dem eigentlichen Löschen, damit bei einem Absturz dazwischen
    // zumindest der Snapshot existiert.
    await archiveItem(
      ENTITY_TYPES.CATEGORY,
      ARCHIVE_REASONS.DELETED,
      { id: match.id, name: match.name },
      { label: match.name }
    );

    try {
      await categoryApi.remove(match.id);
      setCategoryObjects(prev => prev.filter(c => c.id !== match.id));
      setCategories(prev => prev.filter(cat => cat !== categoryName));
    } catch (err) {
      console.error('❌ Kategorie konnte nicht gelöscht werden:', err);
    }
  };

  // Es gibt bewusst keinen PUT/PATCH-Endpoint für Kategorien im Backend
  // (siehe TODO_Archiv_Backend.md Abschnitt 2 - nur GET/POST/DELETE). Rename
  // wird deshalb als "neu anlegen + alte löschen" nachgebildet. Bewusst OHNE
  // Archivierung (weder für die alte noch die neue) - ein reines Umbenennen
  // soll keinen Papierkorb-Eintrag erzeugen, das wäre verwirrend.
  const updateCategory = async (oldName, newName) => {
    if (!newName || !newName.trim()) return false;
    const trimmed = newName.trim();

    if (categories.includes(trimmed) && trimmed !== oldName) {
      return false; // New name already exists
    }

    const match = categoryObjects.find(c => c.name === oldName);
    if (!match) return false;

    try {
      const created = await categoryApi.create(trimmed);
      await categoryApi.remove(match.id);

      setCategoryObjects(prev => [
        ...prev.filter(c => c.id !== match.id),
        created
      ]);
      setCategories(prev => prev.map(cat => (cat === oldName ? created.name : cat)));
      return true;
    } catch (err) {
      console.error('❌ Kategorie konnte nicht umbenannt werden:', err);
      return false;
    }
  };

  // Trägt eine bereits vom Server bekannte Kategorie in den lokalen State ein,
  // OHNE einen weiteren Backend-Call zu machen. Wird von Archive.js nach einem
  // erfolgreichen POST /api/archive/{id}/restore aufgerufen - das Anlegen
  // selbst hat das Backend beim Restore bereits übernommen, ein erneuter
  // addCategory()-Aufruf würde die Kategorie doppelt anlegen.
  const addRestoredCategory = (categoryObj) => {
    if (!categoryObj) return;
    setCategoryObjects(prev => [...prev, categoryObj]);
    setCategories(prev => [...prev, categoryObj.name]);
  };

  const getCategoryStats = (categoryName, bookmarks) => {
    if (!bookmarks || !Array.isArray(bookmarks)) {
      return 0;
    }

    if (categoryName === 'Ohne Kategorie') {
      return bookmarks.filter(bookmark =>
        !bookmark.category ||
        bookmark.category === '' ||
        bookmark.category === null ||
        bookmark.category === undefined
      ).length;
    }

    // More robust category matching - handle different data types, strip prefix, and trim whitespace
    return bookmarks.filter(bookmark => {
      if (!bookmark.category) return false;

      // Convert both to strings, strip "categories." prefix, and trim whitespace for comparison
      const bookmarkCategory = stripCategoryPrefix(String(bookmark.category).trim());
      const targetCategory = stripCategoryPrefix(String(categoryName).trim());

      return bookmarkCategory === targetCategory;
    }).length;
  };

  const value = {
    categories,
    loading,
    addCategory,
    removeCategory,
    updateCategory,
    addRestoredCategory,
    getCategoryStats
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};
